import React from 'react';
import { Paperclip, Truck } from 'lucide-react';
import AttachmentCard from './components/AttachmentCard.js';
import { getCleanStorageFileName } from './utils/storageFileName.js';
import { getSalesShippingSnapshot, getShippingSnapshotAddress } from './utils/shippingSnapshot.js';

const qcChecklistItems = [
  { key: 'qualityChecked', label: 'Material Quality Checked' },
  { key: 'qtyVerified', label: 'Quantity Verified vs BOM' },
  { key: 'properPacking', label: 'Proper Packing & Wrapping' },
  { key: 'laserMarking', label: 'Laser Marking Verified' },
  { key: 'stickersDone', label: 'Stickers Applied' },
  { key: 'docsHanded', label: 'Documents Handed to Driver' },
  { key: 'safetyTag', label: 'Safety Tags Attached' },
  { key: 'informedHO', label: 'Informed HO/Sales' },
];

const formatWeight = (value) => {
  const numericValue = Number.parseFloat(value);
  if (Number.isNaN(numericValue)) return value || '0.00';
  return numericValue.toFixed(2);
};

const QCPreview = ({ formData, printRef, onConfirmDispatch, isSaving }) => {
  const isInterbranchTransfer = formData.typeOfMOC === 'Interbranch Transfer';
  const salesShippingSnapshot = !isInterbranchTransfer ? getSalesShippingSnapshot(formData) : null;
  const referenceNumberLabel = isInterbranchTransfer
    ? 'Transfer No.'
    : formData.typeOfMOC === 'Rental'
      ? 'WO Number'
      : 'PO Number';
  const referenceDateLabel = isInterbranchTransfer
    ? 'Transfer Req. Date'
    : formData.typeOfMOC === 'Rental'
      ? 'WO Date'
      : 'PO DATE';
  const vehicleProofFiles = Array.isArray(formData.vehicleProofFiles) && formData.vehicleProofFiles.length > 0
    ? formData.vehicleProofFiles
    : (formData.vehicleProofUrl ? [{ name: getCleanStorageFileName(formData.vehicleProofUrl), url: formData.vehicleProofUrl }] : []);
  const componentTotals = (formData.components || []).reduce((acc, component) => {
    acc.newQty += parseInt(component.newQty || 0, 10) || 0;
    acc.rentalQty += parseInt(component.rentalQty || 0, 10) || 0;
    return acc;
  }, { newQty: 0, rentalQty: 0 });
  // --- STYLES (moved from QualityCheckDetail.js) ---
  const pdfContainerStyle = { backgroundColor: 'white', padding: '40px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', minHeight: '1100px', width: '100%', boxSizing: 'border-box', color: '#000' };
  const headerTableStyle = { width: '100%', borderCollapse: 'collapse', marginBottom: '15px', border: '1px solid #000' };
  const headerTableCellStyle = { border: '1px solid #000', padding: '8px', fontSize: '13px' };
  const labelCellStyle = { ...headerTableCellStyle, fontWeight: 'bold', backgroundColor: '#f2f2f2' };
  const valueCellStyle = { ...headerTableCellStyle, backgroundColor: '#fff' };
  const pdfTableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '15px', border: '1px solid #000' };
  const pdfThStyle = { border: '1px solid #000', padding: '6px', fontSize: '12px', textAlign: 'center', fontWeight: 'bold' };
  const pdfTdStyle = { border: '1px solid #000', padding: '6px', fontSize: '12px', textAlign: 'center' };
  const signLineStyle = { borderTop: '1px solid #000', width: '180px', textAlign: 'center', paddingTop: '10px', fontSize: '12px', fontWeight: 'bold' };

  const cardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
  
  return (
    <>
      <div ref={printRef} style={pdfContainerStyle}>
          {/* Header Table */}
          <table style={headerTableStyle}>
            <tbody>
              <tr><td colSpan="5" style={{ ...headerTableCellStyle, textAlign: 'center', backgroundColor: '#f1f5f9', fontWeight: '800', fontSize: '18px' }}>PACKAGING/BILL OF MATERIAL</td></tr>
              <tr>
                <td style={labelCellStyle}>Document date</td><td style={valueCellStyle}>{formData.mocDate}</td>
                <td style={labelCellStyle}>Document No.</td><td style={valueCellStyle}>{formData.mocNo}</td>
                <td style={valueCellStyle}><strong>Order No:</strong> {formData.orderNumber || '---'}</td>
              </tr>
              <tr>
                <td style={labelCellStyle}>Type of MOC</td><td style={valueCellStyle}>{formData.typeOfMOC}</td>
                <td style={labelCellStyle}>Document type</td><td style={valueCellStyle}>Delivery</td>
                <td style={{...valueCellStyle, backgroundColor: '#fdf2f2'}}><strong>ALW:</strong> {formatWeight(formData.aluminiumWeight)} kg</td>
              </tr>
              <tr>
                <td style={labelCellStyle}>{referenceNumberLabel}</td><td style={valueCellStyle}>{formData.poNumber}</td>
                <td style={labelCellStyle}>{referenceDateLabel}</td><td style={valueCellStyle}>{formData.poDate}</td>
                <td style={valueCellStyle}><strong>Warehouse:</strong> {formData.warehouse}</td>
              </tr>
              <tr>
                <td style={labelCellStyle}>Consignee</td><td colSpan="3" style={{...valueCellStyle, fontWeight: 'bold'}}>{formData.typeOfMOC === 'Interbranch Transfer' ? (formData.destinationWarehouseName || formData.customerName) : (salesShippingSnapshot?.customerName || formData.customerName)}</td>
                <td rowSpan="4" style={{ ...headerTableCellStyle, textAlign: 'center', verticalAlign: 'middle', width: '120px' }}>
                  <strong>Total Meter</strong><br /><span style={{ fontSize: '30px', fontWeight: '900' }}>{formData.totalMeterNumber}</span>
                </td>
              </tr>
              <tr><td style={labelCellStyle}>Shipping Address</td><td colSpan="3" style={{ ...valueCellStyle, height: '60px', verticalAlign: 'top' }}>{formData.typeOfMOC === 'Interbranch Transfer' ? (formData.destinationWarehouseAddress || formData.shippingAddress) : getShippingSnapshotAddress(salesShippingSnapshot, formData.shippingAddress)}</td></tr>
              <tr><td style={labelCellStyle}>Material Code</td><td colSpan="3" style={valueCellStyle}>{formData.materialCode}</td></tr>
              <tr><td style={labelCellStyle}>Product Summary</td><td colSpan="3" style={valueCellStyle}>{formData.productSummary}</td></tr>
            </tbody>
          </table>

          {/* SECTION 4: ITEM SUMMARY (Mini summary similar to DC) */}
          <table style={{ ...headerTableStyle, borderTop: 'none' }}>
            <tbody>
              <tr>
                <td style={{ ...headerTableCellStyle, width: '33%' }}><strong>Item code:</strong> {formData.materialCode || '---'}</td>
                <td style={{ ...headerTableCellStyle, width: '33%' }}><strong>Tower Height:</strong> {formData.productSummary || '---'}</td>
                <td style={{ ...headerTableCellStyle, width: '34%' }}><strong>Total Meter:</strong> {formData.totalMeterNumber || '---'}</td>
              </tr>
            </tbody>
          </table>

          {/* Component Table */}
          <table style={pdfTableStyle}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={pdfThStyle}>S.No</th><th style={pdfThStyle}>SKU</th><th style={{ ...pdfThStyle, textAlign: 'left' }}>Item Name</th><th style={pdfThStyle}>New Qty</th><th style={pdfThStyle}>Rental Qty</th><th style={pdfThStyle}>Tick</th><th style={pdfThStyle}>Total Weight</th>
              </tr>
            </thead>
            <tbody>
              {formData.components?.map((c, i) => (
                <tr key={i}>
                  <td style={pdfTdStyle}>{i + 1}</td><td style={pdfTdStyle}>{c.sku}</td><td style={{ ...pdfTdStyle, textAlign: 'left' }}>{c.name}</td><td style={pdfTdStyle}>{c.newQty || 0}</td><td style={pdfTdStyle}>{c.rentalQty || 0}</td><td style={pdfTdStyle}><input type="checkbox" checked={false} readOnly /></td><td style={pdfTdStyle}>{((parseInt(c.newQty || 0) + parseInt(c.rentalQty || 0)) * parseFloat(c.weight || 0)).toFixed(2)} kg</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 'bold', backgroundColor: '#f2f2f2' }}>
                <td colSpan="3" style={{ ...pdfTdStyle, textAlign: 'right' }}>TOTAL WEIGHT</td><td style={pdfTdStyle}>{componentTotals.newQty}</td><td style={pdfTdStyle}>{componentTotals.rentalQty}</td><td style={pdfTdStyle}></td><td style={pdfTdStyle}>{parseFloat(formData.totalWeight || 0).toFixed(2)} kg</td>
              </tr>
            </tbody>
          </table>

          {/* Material Checklist */}
          <table style={headerTableStyle}>
            <tbody>
              <tr><td style={labelCellStyle}>Stickers</td><td style={valueCellStyle}>{formData.materialChecklist?.stickers ? 'YES' : 'NO'}</td><td style={labelCellStyle}>Laser Marking</td><td style={valueCellStyle}>{formData.materialChecklist?.laserMarking ? 'YES' : 'NO'}</td><td style={labelCellStyle}>Material Wrapping</td><td style={valueCellStyle}>{formData.materialChecklist?.materialWrapping ? 'YES' : 'NO'}</td></tr>
              <tr><td style={labelCellStyle}>Instructions</td><td colSpan="5" style={{ ...valueCellStyle, minHeight: '50px' }}>{formData.materialChecklist?.materialInstructions || 'None'}</td></tr>
            </tbody>
          </table>

          {/* QC Checklist */}
          <table style={headerTableStyle}>
            <tbody>
              <tr>
                <td colSpan="4" style={{ ...labelCellStyle, textAlign: 'center', fontSize: '14px' }}>
                  QC Checklist
                </td>
              </tr>
              {Array.from({ length: Math.ceil(qcChecklistItems.length / 2) }, (_, rowIndex) => {
                const leftItem = qcChecklistItems[rowIndex * 2];
                const rightItem = qcChecklistItems[rowIndex * 2 + 1];

                return (
                  <tr key={leftItem.key}>
                    <td style={labelCellStyle}>{leftItem.label}</td>
                    <td style={valueCellStyle}>{formData.qcChecklist?.[leftItem.key] ? 'YES' : 'NO'}</td>
                    {rightItem ? (
                      <>
                        <td style={labelCellStyle}>{rightItem.label}</td>
                        <td style={valueCellStyle}>{formData.qcChecklist?.[rightItem.key] ? 'YES' : 'NO'}</td>
                      </>
                    ) : (
                      <td colSpan="2" style={valueCellStyle}>---</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* QC Verification Section */}
          <table style={headerTableStyle}>
            <tbody>
              <tr>
                <td style={labelCellStyle}>Prepared by</td><td style={valueCellStyle}>{formData.preparedBy}</td>
                <td rowSpan="6" style={{ ...headerTableCellStyle, verticalAlign: 'top', fontSize: '11px', width: '45%', borderLeft: '2px solid #000' }}>
                  <div style={{fontWeight: 'bold', marginBottom: '5px', textDecoration: 'underline'}}>NOTICE FOR SALES TEAM:</div>
                  Sales Team, please verify the Material Quantity Against your BOM Before Signing the MOC. Any Discrepancies in the MOC Will Not Be the Responsibility of the Operations Team, As It Has Been Prepared Exactly As Per Your BOM.
                </td>
              </tr>
              <tr><td style={labelCellStyle}>Qty Verified By (Approval)</td><td style={valueCellStyle}>{formData.quantityVerifiedBy}</td></tr>
              <tr><td style={labelCellStyle}>Qty Verified By (Dispatch)</td><td style={valueCellStyle}>{formData.quantityVerifiedByDispatch}</td></tr>
              <tr><td style={labelCellStyle}>Vehicle Number</td><td style={valueCellStyle}>{formData.vehicleNumber}</td></tr>
              <tr><td style={labelCellStyle}>Vehicle Type</td><td style={valueCellStyle}>{formData.vehicleType || '---'}</td></tr>
              <tr><td style={labelCellStyle}>Vehicle In Time</td><td style={valueCellStyle}>{formData.vehicleInTime}</td></tr>
              <tr><td style={labelCellStyle}>Vehicle Out Time</td><td style={valueCellStyle}>{formData.vehicleOutTime}</td></tr>
              <tr><td style={labelCellStyle}>Date</td><td style={valueCellStyle}>{formData.vehicleDate}</td><td style={{ ...headerTableCellStyle, textAlign: 'center', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#f2f2f2' }}>SIGNATURE</td></tr>
            </tbody>
          </table>
          </div>

          {(vehicleProofFiles.length > 0 || formData.deliveryChallanUrl || formData.bomSignedUrl || formData.qcUploadUrl) && (

          <div style={{...cardStyle, marginTop: '20px', maxWidth: '1100px', margin: '20px auto', animation: 'fadeIn 0.3s'}}>
            <h3 style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px'}}>
              <Paperclip size={20} color="#2563eb"/> Document Attachments
            </h3>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '15px'}}>
              {vehicleProofFiles.map((file, index) => (
                <AttachmentCard
                  key={`${file.url}-${index}`}
                  href={file.url}
                  label={`Vehicle Proof ${vehicleProofFiles.length > 1 ? index + 1 : ''}`.trim()}
                  name={file.name || getCleanStorageFileName(file.url)}
                  iconBackgroundColor="#e0f2fe"
                  iconColor="#0284c7"
                />
              ))}
              {formData.deliveryChallanUrl && (
                <AttachmentCard
                  href={formData.deliveryChallanUrl}
                  label="Delivery Challan"
                  name={getCleanStorageFileName(formData.deliveryChallanUrl)}
                  iconBackgroundColor="#ede9fe"
                  iconColor="#5b21b6"
                />
              )}
              {formData.bomSignedUrl && (
                <AttachmentCard
                  href={formData.bomSignedUrl}
                  label="BOM (Signed)"
                  name={getCleanStorageFileName(formData.bomSignedUrl)}
                  iconBackgroundColor="#fee2e2"
                  iconColor="#991b1b"
                />
              )}
              {formData.qcUploadUrl && (
                <AttachmentCard
                  href={formData.qcUploadUrl}
                  label="QC Upload"
                  name={getCleanStorageFileName(formData.qcUploadUrl)}
                  iconBackgroundColor="#dcfce7"
                  iconColor="#166534"
                />
              )}
            </div>
          </div>
        )}
    </>
  );
};

export default QCPreview;
